import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { HeartHandshake, ShieldCheck } from 'lucide-react';

import { AuthLayout } from '@/components/layouts/AuthLayout';
import { ClientAuthBrandPanel } from '@/components/layouts/components/ClientAuthBrandPanel';
import { Alert } from '@/components/ui/alert';
import { getSsoPartner } from '@/features/auth/ssoPartners';
import { startSsoLogin } from '@/features/auth/services/ssoApi';
import {
  AuthFootnote,
  authFootnoteActionClass,
} from '@/features/auth/components/auth-ui/AuthFootnote';
import { AuthHeading } from '@/features/auth/components/auth-ui/AuthHeading';
import { AuthPrimaryButton } from '@/features/auth/components/auth-ui/AuthPrimaryButton';
import { AUTH_COLOR } from '@/features/auth/components/auth-ui/authTokens';

export const Route = createFileRoute('/sso/$slug')({
  component: SsoSlugPage,
});

function SsoSlugPage() {
  const { slug } = useParams({ from: '/sso/$slug' });
  const partner = getSsoPartner(slug);

  return (
    <AuthLayout brandPanel={<ClientAuthBrandPanel />} formSide="right">
      {partner ? (
        <div className="flex flex-col items-center gap-8 text-center">
          <header className="flex flex-col items-center gap-5">
            <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#f28155] to-[#f54600] shadow-[0_1px_2px_rgba(3,68,92,0.06)] ring-1 ring-[#e6e6e6]">
              <HeartHandshake className="size-7 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-accent-ink/50 font-sans text-xs font-medium tracking-[0.12em] uppercase">
                {partner.displayName} · {partner.tagline}
              </span>
              <AuthHeading
                size="sm"
                title="Welcome to your wellbeing space"
                subtitle={`Sign in with your ${partner.displayName} account to book sessions and reach support, whenever you need it.`}
              />
            </div>
          </header>
          <div className="flex flex-col gap-3">
            <AuthPrimaryButton
              type="button"
              onClick={() => {
                startSsoLogin(partner.slug);
              }}
            >
              Continue with {partner.displayName}
            </AuthPrimaryButton>
            <p className="text-accent-ink/45 flex items-center justify-center gap-1.5 font-sans text-xs">
              <ShieldCheck className="size-3.5 shrink-0" strokeWidth={1.5} />
              You’ll be redirected to {partner.displayName} to sign in securely.
            </p>
          </div>
          <span className="h-px w-full bg-(--color-border)" aria-hidden="true" />
          {/* <AuthFootnote
            prompt={`Not a ${partner.displayName} student?`}
            action={
              <Link
                to="/client/login"
                className={authFootnoteActionClass}
                style={{ color: AUTH_COLOR.blue }}
              >
                Use email login
              </Link>
            }
          /> */}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <AuthHeading
            size="sm"
            title="Unknown sign-in link"
            subtitle="We don’t recognise this sign-in link. Please double-check the address your institution shared with you."
          />
          <Alert variant="error">This sign-in link is not valid.</Alert>
          <AuthFootnote
            prompt="Back to"
            action={
              <Link
                to="/client/login"
                className={authFootnoteActionClass}
                style={{ color: AUTH_COLOR.blue }}
              >
                Login
              </Link>
            }
          />
        </div>
      )}
    </AuthLayout>
  );
}
